import { PageHero } from "@/components/site/sections";
import { Reveal } from "@/components/site/ui";
import { IMAGES } from "@/data/content";

// India-compliant Privacy Policy references:
//  • Information Technology Act, 2000 (Section 43A) and the SPDI Rules, 2011
//  • Digital Personal Data Protection Act, 2023 ("DPDP Act")
//  • Consumer Protection Act, 2019 and the CP (E-Commerce) Rules, 2020
// Effective date is set at build time; keep a note when updating.

const EFFECTIVE_DATE = "9 September 2026";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-32 mt-14">
    <h2 className="font-head text-2xl sm:text-3xl font-medium tracking-tight text-[#1C1A17]">
      {title}
    </h2>
    <div className="mt-4 text-[#3F3B34] leading-[1.8] space-y-4">{children}</div>
  </section>
);

const TOC = [
  { id: "who-we-are", label: "Who we are" },
  { id: "data-we-collect", label: "Personal data we collect" },
  { id: "purpose", label: "Purpose & legal basis" },
  { id: "consent", label: "Consent & withdrawal" },
  { id: "cookies", label: "Cookies & analytics" },
  { id: "sharing", label: "How we share your data" },
  { id: "retention", label: "Retention" },
  { id: "security", label: "Security safeguards" },
  { id: "your-rights", label: "Your rights as a Data Principal" },
  { id: "children", label: "Children's data" },
  { id: "grievance", label: "Grievance Officer" },
  { id: "changes", label: "Changes to this policy" },
];

export default function PrivacyPolicy() {
  return (
    <>
      <PageHero
        overline="Legal"
        title="Privacy Policy"
        subtitle="How Spherio Living Private Limited collects, uses, stores and protects your personal data — in accordance with Indian law."
        image={IMAGES.interior3}
      />

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14">
        {/* Sticky TOC */}
        <aside className="lg:col-span-4 lg:sticky lg:top-32 h-max">
          <div className="rounded-2xl border border-black/10 p-6">
            <div className="text-xs tracking-[0.2em] uppercase text-[#5C574F] font-bold">
              On this page
            </div>
            <ul className="mt-4 space-y-2.5 text-sm">
              {TOC.map((t) => (
                <li key={t.id}>
                  <a
                    href={`#${t.id}`}
                    className="text-[#1C1A17] hover:text-[#B8935A] transition-colors"
                  >
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-6 border-t border-black/10 text-xs text-[#5C574F]">
              Effective: {EFFECTIVE_DATE}
              <br />
              Version 1.0
            </div>
          </div>
        </aside>

        {/* Body */}
        <div className="lg:col-span-8" data-testid="privacy-policy-content">
          <Reveal>
            <p className="text-lg leading-relaxed text-[#3F3B34]">
              This Privacy Policy explains how <strong>Spherio Living Private Limited</strong>{" "}
              ("Spherio Living", "we", "us" or "our"), a company incorporated in India with
              its registered office at SCO No. 222, Shopping Square 1, Sector D, Sushant Golf City,
              Lucknow 226030, Uttar Pradesh, collects, uses, discloses and protects the
              personal data you share with us when you visit{" "}
              <a
                href="https://spherioliving.com"
                className="text-[#1C1A17] underline underline-offset-4"
              >
                spherioliving.com
              </a>{" "}
              or engage our services. This Policy is published in compliance with the
              Information Technology Act, 2000 and the rules made thereunder (including the
              SPDI Rules, 2011), the Digital Personal Data Protection Act, 2023, and other
              applicable Indian laws.
            </p>
          </Reveal>

          <Section id="who-we-are" title="1. Who we are">
            <p>
              Spherio Living is an interior design and home-improvement company offering
              modular kitchens, wardrobes, complete home interiors, architectural design,
              renovation and smart-home solutions. For the purpose of this Policy, we act
              as the "Data Fiduciary" (under the DPDP Act) / "Body Corporate" (under the
              SPDI Rules) in respect of personal data collected via our website and lead
              channels.
            </p>
          </Section>

          <Section id="data-we-collect" title="2. Personal data we collect">
            <p>Depending on how you interact with us, we may collect:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Identity & contact data</strong> — name, email address, phone
                number, city, service of interest, message content.
              </li>
              <li>
                <strong>Account data</strong> — email, hashed password, Google account
                identifiers (if you sign in via Google), profile picture, and session
                tokens.
              </li>
              <li>
                <strong>Project data</strong> — home size, room configuration, budget
                range and preferences you share when using the Instant Quote tool or
                during consultation.
              </li>
              <li>
                <strong>Technical data</strong> — IP address, browser type, device
                information, referrer URL, pages visited, timestamps and cookies (see
                "Cookies & analytics" below).
              </li>
              <li>
                <strong>Payment data (if applicable)</strong> — we do not store card
                numbers or bank credentials on our servers; payments (when enabled) are
                processed by a PCI-DSS compliant payment aggregator.
              </li>
            </ul>
            <p>
              We do not knowingly collect "Sensitive Personal Data or Information" (SPDI)
              — such as passwords stored in plaintext, financial information beyond what
              is strictly necessary for a transaction, health data or biometric data —
              other than as strictly required to deliver our services.
            </p>
          </Section>

          <Section id="purpose" title="3. Purpose & legal basis">
            <p>We process your personal data for the following specified purposes:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>To respond to your enquiry, quote request or consultation booking.</li>
              <li>To provide the services you have engaged us for and to keep you informed of project progress.</li>
              <li>To create and manage your customer account and portal access.</li>
              <li>To send transactional emails (booking confirmations, project milestone updates, password resets).</li>
              <li>To send marketing communications where you have explicitly consented (you may withdraw consent at any time).</li>
              <li>To improve our website, prevent fraud and secure our systems.</li>
              <li>To comply with legal, tax and regulatory obligations under Indian law.</li>
            </ul>
            <p>
              Under the DPDP Act, our lawful basis is (a) your consent, or (b) certain
              "legitimate uses" specified in Section 7 (such as responding to your
              enquiry, providing a service you have requested, or complying with law).
            </p>
          </Section>

          <Section id="consent" title="4. Consent & withdrawal">
            <p>
              When you submit a form on our website, you consent to the processing of
              your personal data for the purpose stated at the point of collection. Your
              consent is free, specific, informed and unambiguous.
            </p>
            <p>
              You can withdraw your consent at any time by emailing us at{" "}
              <a
                href="mailto:hello@spherioliving.com"
                className="text-[#1C1A17] underline underline-offset-4"
              >
                hello@spherioliving.com
              </a>
              . Withdrawal will not affect the lawfulness of processing carried out prior
              to withdrawal, and may prevent us from providing certain services.
            </p>
          </Section>

          <Section id="cookies" title="5. Cookies & analytics">
            <p>
              Our website uses essential cookies (for session management and secure
              authentication) and may use analytics cookies (to understand aggregated
              usage patterns). Essential cookies are set automatically and are required
              for the site to function; you can control non-essential cookies through
              your browser settings. Disabling essential cookies may prevent parts of the
              site (such as the customer portal) from working correctly.
            </p>
          </Section>

          <Section id="sharing" title="6. How we share your data">
            <p>
              We do not sell your personal data. We share limited personal data only in
              the following circumstances and only with recipients bound by
              confidentiality and data-protection obligations:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Service providers</strong> — hosting, email delivery (Resend),
                Google Sign-In, analytics and (when enabled) payment aggregators.
              </li>
              <li>
                <strong>Project partners</strong> — architects, factory partners and
                installation teams engaged on your project, strictly on a need-to-know basis.
              </li>
              <li>
                <strong>Legal & regulatory authorities</strong> — where required to
                comply with a lawful order, court process, or applicable law.
              </li>
              <li>
                <strong>Corporate transactions</strong> — in the event of a merger,
                acquisition or sale of assets, subject to equivalent privacy protection.
              </li>
            </ul>
            <p>
              Where any recipient is located outside India, we rely on contractual
              safeguards to ensure your data continues to be protected at a standard
              comparable to Indian law.
            </p>
          </Section>

          <Section id="retention" title="7. Retention">
            <p>
              We retain your personal data only for as long as necessary to fulfil the
              purposes for which it was collected, or as required by applicable law
              (typically 3–8 years for financial and tax records under Indian law).
              Marketing consents are retained until you withdraw them. When the
              retention period ends, we erase or anonymise your data.
            </p>
          </Section>

          <Section id="security" title="8. Security safeguards">
            <p>
              We implement "reasonable security practices and procedures" as required
              under Section 43A of the IT Act and Rule 8 of the SPDI Rules, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encryption of data in transit (HTTPS/TLS) and hashed password storage (bcrypt).</li>
              <li>Role-based access controls and audit logging for admin operations.</li>
              <li>Regular back-ups and vulnerability patching of our stack.</li>
              <li>Least-privilege data access for our team members.</li>
              <li>Prompt notification to affected users and the Data Protection Board in the event of a personal data breach, as required under the DPDP Act.</li>
            </ul>
          </Section>

          <Section id="your-rights" title="9. Your rights as a Data Principal">
            <p>Subject to applicable law, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access the personal data we hold about you and a summary of processing.</li>
              <li>Correct or complete inaccurate or incomplete data.</li>
              <li>Erase personal data that is no longer necessary for the specified purpose (subject to legal retention obligations).</li>
              <li>Withdraw consent at any time.</li>
              <li>Nominate another individual to exercise your rights in the event of death or incapacity (as provided under the DPDP Act).</li>
              <li>Register a grievance and, where dissatisfied with our response, escalate to the Data Protection Board of India.</li>
            </ul>
            <p>
              To exercise any of these rights, please write to our Grievance Officer
              (details below). We will respond within the timelines mandated by law.
            </p>
          </Section>

          <Section id="children" title="10. Children's data">
            <p>
              Our services are intended for individuals aged 18 and above. We do not
              knowingly collect personal data of children (individuals below the age of
              18) without verifiable parental or guardian consent, as required under the
              DPDP Act. If you believe we have inadvertently collected such data, please
              contact us and we will delete it promptly.
            </p>
          </Section>

          <Section id="grievance" title="11. Grievance Officer">
            <p>
              In accordance with the Information Technology Act, 2000 and Rule 5(9) of
              the SPDI Rules (and Section 8(9) of the DPDP Act), the details of our
              Grievance Officer are:
            </p>
            <div className="rounded-xl bg-[#F5F1EA] p-6 mt-2">
              <div className="text-sm font-semibold text-[#1C1A17]">
                Grievance Officer, Spherio Living Private Limited
              </div>
              <div className="text-sm text-[#3F3B34] mt-1">
                SCO No. 222, Shopping Square 1, Sector D, Sushant Golf City,<br />
                Lucknow 226030, Uttar Pradesh, India
              </div>
              <div className="text-sm text-[#3F3B34] mt-3">
                Email:{" "}
                <a
                  href="mailto:hello@spherioliving.com"
                  className="text-[#1C1A17] underline underline-offset-4"
                >
                  hello@spherioliving.com
                </a>
                <br />
                Phone: +91 7388047001
                <br />
                Working hours: Mon – Sat, 10 AM – 6 PM IST
              </div>
            </div>
            <p>
              We will acknowledge your grievance within 24 hours and endeavour to resolve
              it within 15 days of receipt, or such other period as may be prescribed by
              law.
            </p>
          </Section>

          <Section id="changes" title="12. Changes to this Policy">
            <p>
              We may update this Policy from time to time to reflect changes in law or
              our practices. The updated version will be posted on this page with a
              revised effective date. Where the changes are material, we will make
              reasonable efforts to notify you (for example, by email or a prominent
              notice on the site).
            </p>
          </Section>
        </div>
      </section>
    </>
  );
}
